import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApexOptions } from 'apexcharts'
import { KTIcon } from '../../../_metronic/helpers'
import { Tr } from '../../../_metronic/i18n/Tr'
import { BOOKABLE_CATEGORIES } from '../../../reusable'
import { ToastContainer } from 'react-toastify'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import moment from 'moment'
import Select from 'react-select'

import ChartCard from './components/ChartCard'
import { selectStyles } from './components/selectStyles'
import { useOccupancyRates, useListingList, useYearList } from './useOccupancyRates'

type TabType = 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'CUSTOM'

const TAB_OPTIONS: { value: TabType; label: string }[] = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'CUSTOM', label: 'Custom' },
]

// ─── Tab Buttons ───
const TabBar = React.memo<{
  activeTab: TabType
  onTabChange: (tab: TabType) => void
  t: (key: string) => string
}>(({ activeTab, onTabChange, t }) => (
  <div className="d-flex flex-wrap gap-2">
    {TAB_OPTIONS.map(tab => (
      <button
        key={tab.value}
        className={`btn btn-sm ${activeTab === tab.value ? 'btn-primary' : 'btn-light'}`}
        onClick={() => onTabChange(tab.value)}
      >
        {t(tab.label)}
      </button>
    ))}
  </div>
))

// ─── Navigation for Week/Month ───
const NavButtons = React.memo<{
  onPrev: () => void
  onNext: () => void
  label?: string
}>(({ onPrev, onNext, label }) => (
  <div className="d-flex align-items-center gap-3">
    <button className="btn btn-sm btn-icon btn-light-primary" onClick={onPrev}>
      <KTIcon iconName="arrow-left" className="fs-3" />
    </button>
    {label ? <span className="fw-bold text-gray-800 fs-5">{label}</span> : null}
    <button className="btn btn-sm btn-icon btn-light-primary" onClick={onNext}>
      <KTIcon iconName="arrow-right" className="fs-3" />
    </button>
  </div>
))

// ─── Main Component ───
const OccupancyRates: FC = () => {
  const tFn = Tr()
  const tRef = useRef(tFn)
  tRef.current = tFn
  const t = useCallback((key: string) => tRef.current(key), [])

  const yearList = useYearList()

  // Filters
  const [selectedCategory, setSelectedCategory] = useState<any>(BOOKABLE_CATEGORIES[0])
  const [selectedListing, setSelectedListing] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<TabType>('MONTHLY')

  // Time navigation
  const [weekStart, setWeekStart] = useState(moment().startOf('isoWeek').format('YYYY-MM-DD'))
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'))
  const [selectedYear, setSelectedYear] = useState<any>({ value: String(moment().year()), label: String(moment().year()) })
  const [fromDate, setFromDate] = useState(moment().startOf('month').format('YYYY-MM-DD'))
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'))
  const [customRange, setCustomRange] = useState<Date[]>([])

  // Listing list based on category
  const listingList = useListingList(selectedCategory?.value || undefined)

  // Occupancy data
  const { data, isLoading } = useOccupancyRates(
    selectedCategory, selectedListing, activeTab,
    weekStart, selectedMonth, selectedYear, fromDate, toDate,
  )

  useEffect(() => {
    document.title = 'Insayr | Occupancy Rates'
  }, [])

  // Reset listing when category changes
  const handleCategoryChange = useCallback((val: any) => {
    setSelectedCategory(val ?? BOOKABLE_CATEGORIES[0])
    setSelectedListing(null)
  }, [])

  // Week navigation
  const handlePrevWeek = useCallback(() => {
    setWeekStart(prev => moment(prev).subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'))
  }, [])
  const handleNextWeek = useCallback(() => {
    setWeekStart(prev => moment(prev).add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'))
  }, [])

  // Month navigation
  const handlePrevMonth = useCallback(() => {
    setSelectedMonth(prev => moment(prev).subtract(1, 'month').format('YYYY-MM'))
  }, [])
  const handleNextMonth = useCallback(() => {
    setSelectedMonth(prev => moment(prev).add(1, 'month').format('YYYY-MM'))
  }, [])

  // Week label
  const weekLabel = useMemo(() => {
    const start = moment(weekStart)
    const end = moment(weekStart).endOf('isoWeek')
    return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`
  }, [weekStart])

  // Chart options (memoized)
  const totalBars = data?.rates?.length ?? 0

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
    series: [{ name: t('Occupancy'), data: data?.rates ?? [] }],
    xaxis: {
      categories: data?.categories ?? [],
      labels: {
        style: { fontSize: '11px' },
        rotate: totalBars > 12 ? -45 : 0,
        rotateAlways: totalBars > 12,
        hideOverlappingLabels: true,
        maxHeight: 80,
      },
      tickAmount: totalBars > 31 ? 15 : undefined,
    },
    yaxis: {
      labels: {
        style: { fontSize: '12px' },
        formatter: (val: number) => `${Math.round(val)}%`,
      },
      max: 100,
    },
    colors: ['#65C098'],
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: totalBars > 20 ? '85%' : totalBars > 12 ? '65%' : '40%',
      },
    },
    dataLabels: { enabled: false },
    tooltip: {
      theme: 'light',
      y: { formatter: (val: number) => `${val}%` },
    },
    grid: { borderColor: '#f1f3f8', strokeDashArray: 4 },
  }), [data, totalBars])

  const hasData = (data?.rates?.length ?? 0) > 0

  return (
    <>
      <ToastContainer />

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-6">
        <div className="card-body py-5 px-6">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <div style={{ minWidth: 200 }}>
                <Select
                  options={BOOKABLE_CATEGORIES}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  placeholder={t('Select Category')}
                  styles={selectStyles}
                />
              </div>
              {listingList.length > 0 ? (
                <div style={{ minWidth: 220 }}>
                  <Select
                    options={listingList}
                    value={selectedListing}
                    onChange={(val: any) => setSelectedListing(val)}
                    isClearable
                    placeholder={t('All Listings')}
                    styles={selectStyles}
                  />
                </div>
              ) : null}
            </div>
            <TabBar activeTab={activeTab} onTabChange={setActiveTab} t={t} />
          </div>
        </div>
      </div>

      {/* Time Navigation */}
      <div className="card border-0 shadow-sm mb-6">
        <div className="card-body py-4 px-6">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            {activeTab === 'WEEKLY' ? (
              <NavButtons onPrev={handlePrevWeek} onNext={handleNextWeek} label={weekLabel} />
            ) : null}

            {activeTab === 'MONTHLY' ? (
              <NavButtons onPrev={handlePrevMonth} onNext={handleNextMonth} label={moment(selectedMonth).format('MMMM YYYY')} />
            ) : null}

            {activeTab === 'YEARLY' ? (
              <div style={{ minWidth: 150 }}>
                <Select
                  options={yearList}
                  value={selectedYear}
                  onChange={(val: any) => setSelectedYear(val)}
                  placeholder={t('Select Year')}
                  styles={selectStyles}
                />
              </div>
            ) : null}

            {activeTab === 'CUSTOM' ? (
              <div className="d-flex align-items-center position-relative">
                <KTIcon iconName="calendar" className="fs-3 position-absolute ms-4 text-gray-500" />
                <Flatpickr
                  className="form-control form-control-solid w-275px ps-12 fs-7"
                  placeholder={t('Select date range')}
                  value={customRange}
                  onChange={(dates: Date[]) => {
                    if (dates?.length === 2) {
                      setCustomRange(dates)
                      setFromDate(moment(dates[0]).format('YYYY-MM-DD'))
                      setToDate(moment(dates[1]).format('YYYY-MM-DD'))
                    }
                  }}
                  options={{ mode: 'range' as const, dateFormat: 'd M Y', monthSelectorType: 'static' as const }}
                />
              </div>
            ) : null}

            {isLoading ? (
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">{t('Loading...')}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Overall Rate + Chart */}
      {hasData ? (
        <>
          <div className="card border-0 shadow-sm mb-6">
            <div className="card-body d-flex align-items-center gap-4 py-5 px-6">
              <div className="d-flex align-items-center justify-content-center rounded bg-light-success"
                style={{ width: 52, height: 52, minWidth: 52 }}>
                <KTIcon iconName="chart-simple" className="fs-1 text-success" />
              </div>
              <div>
                <div className="text-gray-500 fs-7 fw-semibold">{t('Overall Occupancy Rate')}</div>
                <div className="fw-bolder text-gray-900 fs-2">{data?.overallRate ?? 0}%</div>
              </div>
            </div>
          </div>
          <ChartCard
            title={t('Occupancy Rate')}
            chartId="occupancy-rate-chart"
            options={chartOptions}
            height={450}
          />
        </>
      ) : !isLoading ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-15">
            <KTIcon iconName="chart-simple" className="fs-3tx text-gray-300 mb-4" />
            <div className="text-gray-500 fs-5 fw-semibold">{t('No occupancy data available')}</div>
            <div className="text-gray-400 fs-7 mt-2">{t('Try changing the category or time period')}</div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default OccupancyRates
